/**
 * AttachmentsUI
 *
 * Renders the attachments panel for the artifact in scope — a form version or an
 * entity record — writing to that document's `attachments` map. Consumer: the
 * `attachments` layout element in a form or entity body.
 *
 * Form-side reads are three narrow leaves off `formStore.state`
 * (`form.data.formNumber`, `formVersion.data.parentFormNumber`,
 * `formVersion.data.formVersionNumber`) plus `data.attachments` through
 * `useCurrentArtifact`; writes go through `formStore.getFormVersionPersist()`.
 * `useFormStore()` is banned here: its spread of `formVersion.data` re-rendered
 * this panel on every keystroke in the form (SYD-2252).
 *
 * The capacity caption is rendered here, not inside `Attachments`, for the same
 * reason the read-only sentence is decided here: it must not print into a PDF
 * export, and `isPdfExportMode` is only knowable at this end of the wire. It is
 * the one thing this panel shows a reader who cannot write — how large a file
 * may be and how full the document is are facts about the document, and a
 * reader deciding whether to ask someone else to attach something is entitled
 * to both. Its two figures come from the constants the server enforces, so the
 * number on screen is the number that refuses an upload.
 *
 * `shellDisabled` and `readOnlyReason` are deliberately different sets, and
 * collapsing them back into one boolean is the bug this file guards against.
 * `shellDisabled` says what is interactive; `readOnlyReason` says what the
 * reader is told. A PDF export disables everything and explains nothing — a
 * rendered document must never tell its reader they lack permission to edit it.
 * The reason is born far from here, in `FormUI` and `Entity`, and the export
 * portal re-renders `FormUI`, so the export gate has to be applied at this end
 * of the wire (SYD-2455).
 *
 * `extractScheduleHost` is resolved here, from the artifact in scope: a form
 * version is a `form` host carrying the order Extract schedule writes to, and
 * everything else is a `record` host. `'none'` and a form with no selected
 * version may take `record` safely: their `data` is null, so they list no rows
 * and no Extract button is ever shown for them.
 */

import { FC, memo, useCallback, useMemo } from 'react';
import { Typography } from 'antd';
import { useSnapshot } from 'valtio';
import { UIAttachments, ArtifactAttachments } from '#types/System.types';
import type { CommunicationRecord } from '#types';
import FlexCol from '../../atoms/FlexCol';
import FieldLabel from '../../atoms/FieldLabel';
import { Attachments } from './Attachments';
import { resolveAttachmentCapacityCaption } from './resolveAttachmentCapacityCaption';
import { useCurrentArtifact } from '../../../use/useCurrentArtifact';
import type { AttachmentsCommunicationLogOptions } from './Attachments';
import { formStore, useEntityRecordScope } from '../../../store';
import { useCommunicationLogBundles } from '../fields/CommunicationLog/use/useCommunicationLogBundles';
import { CommunicationLogBundles } from '../fields/CommunicationLog/services/CommunicationLogBundles';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import type { ReadOnlyReason } from '../readOnlyReason';
import type { ExtractScheduleHost } from './index.types';

/**
 * Props for the AttachmentsUI component
 */
interface AttachmentsUIProps {
  /** Configuration object containing attachment properties */
  uiAttachments: UIAttachments;
  /** Whether attachments should be in read-only/disabled state */
  disabled?: boolean;
  /**
   * Why the artifact cannot be written, when that is worth telling the reader.
   * Only a genuine permission denial arrives here; a user-toggled lock, a host
   * lock and an unloaded record all disable the panel silently.
   */
  readOnlyReason?: ReadOnlyReason;
}

/**
 * A component for managing attachments using the dedicated
 * attachments property in FormVersion or EntityRecord.
 *
 * This component directly uses the attachments property for storage
 * instead of using fields.
 */
const AttachmentsUI: FC<AttachmentsUIProps> = memo(
  ({ uiAttachments, disabled = false, readOnlyReason }) => {
    const { isPdfExportMode } = usePdfExportContext();
    const artifact = useCurrentArtifact();
    const insideFormContext = artifact.insideFormContext;
    const insideEntityContext = artifact.insideEntityContext;
    const data = artifact.kind === 'none' ? null : artifact.data;
    const formId = artifact.kind === 'form' ? artifact.formId : undefined;
    const versionId = artifact.kind === 'form' ? artifact.versionId : undefined;
    const formSnap = useSnapshot(formStore.state);
    const rawEmailAliasContext = {
      formNumber:
        formSnap.form.data?.formNumber ??
        formSnap.formVersion.data?.parentFormNumber,
      formVersionNumber: formSnap.formVersion.data?.formVersionNumber,
    };
    const scope = useEntityRecordScope();
    const debouncedSaveEntityRecord = scope.debouncedSetEntityRecord;
    const communicationLogFieldId =
      (uiAttachments.props?.communicationLogFieldId as string | undefined) ||
      'communicationLog';
    const formVersionId = versionId;
    const bundlesEnabled =
      insideFormContext && Boolean(formId) && Boolean(formVersionId);

    const { bundledRecords } = useCommunicationLogBundles({
      formId,
      formVersionId,
      fieldId: communicationLogFieldId,
      enabled: bundlesEnabled,
    });

    /**
     * Updates the form or entity when attachments change
     * @param newAttachments The updated attachments map
     */
    const setAttachments = useCallback(
      (newAttachments: ArtifactAttachments) => {
        if (disabled) {
          return;
        }

        if (insideFormContext) {
          void formStore.getFormVersionPersist()?.setMerge(
            {
              attachments: newAttachments,
            },
            { debounce: true },
          );
        } else if (insideEntityContext && debouncedSaveEntityRecord) {
          debouncedSaveEntityRecord({
            attachments: newAttachments,
          });
        }
      },
      [
        insideFormContext,
        insideEntityContext,
        debouncedSaveEntityRecord,
        disabled,
      ],
    );

    const communicationLogOptions = useMemo<
      AttachmentsCommunicationLogOptions | undefined
    >(() => {
      if (!bundlesEnabled || !versionId) {
        return undefined;
      }

      const currentLogEntryCount = bundledRecords.length;

      const appendToLog = async (record: CommunicationRecord) => {
        if (!formId || !formVersionId) {
          return { success: false, truncated: false };
        }
        try {
          await CommunicationLogBundles.appendRecord({
            formId,
            formVersionId,
            fieldId: communicationLogFieldId,
            record,
          });
          return { success: true, truncated: false };
        } catch (error) {
          console.warn(
            'Failed to add record to communication log bundles:',
            error,
          );
          return { success: false, truncated: false };
        }
      };

      return {
        parentDocId: versionId,
        currentLogEntryCount,
        addEmailToLog: appendToLog,
        addAttachmentRenameToLog: appendToLog,
      };
    }, [
      bundledRecords,
      bundlesEnabled,
      communicationLogFieldId,
      formId,
      formVersionId,
      versionId,
    ]);

    const hasArtifactScope = insideFormContext || insideEntityContext;
    const attachments: ArtifactAttachments = data?.attachments || {};
    const shellDisabled =
      disabled || isPdfExportMode || !data || !hasArtifactScope;
    const reportedReadOnlyReason = isPdfExportMode ? undefined : readOnlyReason;
    const extractScheduleHost: ExtractScheduleHost =
      artifact.kind === 'form' && artifact.versionId
        ? {
            kind: 'form',
            order: {
              formId: artifact.formId,
              formVersionId: artifact.versionId,
            },
          }
        : { kind: 'record' };

    return (
      <FlexCol
        gap={0}
        {...uiAttachments.props}
        id="attachments-ui-container"
        aria-label="Attachments section"
      >
        <FieldLabel
          label="Attachments"
          required={uiAttachments.required}
          tooltip="Files attached to this document"
        />
        <Attachments
          value={attachments}
          onChange={setAttachments}
          disabled={shellDisabled}
          readOnlyReason={reportedReadOnlyReason}
          communicationLogOptions={communicationLogOptions}
          rawEmailAliasContext={rawEmailAliasContext}
          extractScheduleHost={extractScheduleHost}
        />
        {!isPdfExportMode && (
          <Typography.Text id="attachments-capacity-caption" type="secondary">
            {resolveAttachmentCapacityCaption(Object.keys(attachments).length)}
          </Typography.Text>
        )}
      </FlexCol>
    );
  },
);

AttachmentsUI.displayName = 'AttachmentsUI';

export default AttachmentsUI;
